import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dumbbell,
  ArrowLeft,
  TrendingDown,
  Activity,
  Target,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Edit2,
  TrendingUp,
  FileDown,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const FOCUS_TYPES = ['FORCE', 'POWER', 'VELOCITY'];

const VBTTestDetailPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { api, hasRole } = useAuth();
  const { t, language } = useLanguage();

  const [test, setTest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);

  const canEdit = hasRole(['ADMIN', 'SNC']);

  // Prescription form
  const [rxForm, setRxForm] = useState({
    date: new Date().toISOString().split('T')[0],
    focus: 'POWER',
    sets: 4,
    reps: 5,
    rest_seconds: 180,
    load_min: '',
    load_max: '',
    vel_min: '',
    vel_max: '',
    velocity_stop: '',
    notes: '',
  });

  const fetchTest = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/vbt/tests/${testId}`);
      setTest(response.data);

      // Fetch comparison with previous test
      try {
        const compRes = await api.get(`/vbt/tests/${testId}/comparison`);
        setComparison(compRes.data);
      } catch {
        setComparison(null);
      }

      // Pre-fill prescription form based on test data
      if (response.data.lv_profile) {
        const l0 = response.data.lv_profile.l0;
        // Default to ~75% for POWER zone
        const loadMin = Math.round(l0 * 0.65);
        const loadMax = Math.round(l0 * 0.80);
        setRxForm((prev) => ({
          ...prev,
          load_min: loadMin.toString(),
          load_max: loadMax.toString(),
          vel_min: '0.50',
          vel_max: '0.75',
        }));
      }
    } catch (err) {
      console.error('Failed to fetch test:', err);
      toast.error('Failed to load test');
    } finally {
      setIsLoading(false);
    }
  }, [api, testId]);

  useEffect(() => {
    fetchTest();
  }, [fetchTest]);

  const handleDelete = async () => {
    try {
      await api.delete(`/vbt/tests/${testId}`);
      toast.success(language === 'IT' ? 'Test eliminato' : 'Test deleted');
      navigate('/staff/vbt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete test');
    }
  };

  const handleCreatePrescription = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        athlete_id: test.athlete_id,
        team_id: test.team_id,
        test_id: testId,
        date: rxForm.date,
        lift_type: test.lift_type,
        focus: rxForm.focus,
        sets: parseInt(rxForm.sets),
        reps: parseInt(rxForm.reps),
        rest_seconds: parseInt(rxForm.rest_seconds),
        load_range_kg: [parseFloat(rxForm.load_min), parseFloat(rxForm.load_max)],
        velocity_target: [parseFloat(rxForm.vel_min), parseFloat(rxForm.vel_max)],
        velocity_stop: rxForm.velocity_stop ? parseFloat(rxForm.velocity_stop) : null,
        notes: rxForm.notes || null,
      };

      await api.post('/vbt/prescriptions', payload);
      toast.success(language === 'IT' ? 'Prescrizione creata!' : 'Prescription created!');
      setShowPrescriptionDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create prescription');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">{language === 'IT' ? 'Test non trovato' : 'Test not found'}</p>
      </div>
    );
  }

  const profile = test.lv_profile;
  const quality = test.quality_report;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/staff/vbt')}
            className="text-zinc-400 hover:text-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
              <Dumbbell className="w-6 h-6 text-amber-500" />
              {test.lift_type}{test.custom_lift_name ? `: ${test.custom_lift_name}` : ''}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {test.athlete_name} • {test.team_code} • {test.date}
            </p>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/vbt/tests/${testId}/report`;
                const token = localStorage.getItem('fb_access_token');
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `VBT_Report_${test.athlete_name}_${test.date}.pdf`;
                    a.click();
                  });
              }}
              className="border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-zinc-600"
              data-testid="download-report-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {language === 'IT' ? 'Scarica Report' : 'Download Report'}
            </Button>
            <Dialog open={showPrescriptionDialog} onOpenChange={setShowPrescriptionDialog}>
              <DialogTrigger asChild>
                <Button className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="create-rx-btn">
                  <Target className="w-4 h-4 mr-2" />
                  {language === 'IT' ? 'Crea Prescrizione' : 'Create Prescription'}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-zinc-100">
                    {language === 'IT' ? 'Nuova Prescrizione' : 'New Prescription'}
                  </DialogTitle>
                  <DialogDescription className="text-zinc-500">
                    {language === 'IT' ? 'Basata sul profilo L-V del test' : 'Based on the L-V profile from this test'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreatePrescription} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Data' : 'Date'}</Label>
                      <Input
                        type="date"
                        value={rxForm.date}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, date: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Focus</Label>
                      <Select
                        value={rxForm.focus}
                        onValueChange={(v) => setRxForm((prev) => ({ ...prev, focus: v }))}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {FOCUS_TYPES.map((f) => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Sets</Label>
                      <Input
                        type="number"
                        min="1"
                        value={rxForm.sets}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, sets: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Reps</Label>
                      <Input
                        type="number"
                        min="1"
                        value={rxForm.reps}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, reps: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rest (s)</Label>
                      <Input
                        type="number"
                        min="30"
                        step="30"
                        value={rxForm.rest_seconds}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, rest_seconds: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Carico Min (kg)' : 'Load Min (kg)'}</Label>
                      <Input
                        type="number"
                        step="2.5"
                        value={rxForm.load_min}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, load_min: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Carico Max (kg)' : 'Load Max (kg)'}</Label>
                      <Input
                        type="number"
                        step="2.5"
                        value={rxForm.load_max}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, load_max: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Vel Target Min (m/s)' : 'Vel Target Min (m/s)'}</Label>
                      <Input
                        type="number"
                        step="0.05"
                        value={rxForm.vel_min}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, vel_min: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Vel Target Max (m/s)' : 'Vel Target Max (m/s)'}</Label>
                      <Input
                        type="number"
                        step="0.05"
                        value={rxForm.vel_max}
                        onChange={(e) => setRxForm((prev) => ({ ...prev, vel_max: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'IT' ? 'Velocity Stop (m/s, opzionale)' : 'Velocity Stop (m/s, optional)'}</Label>
                    <Input
                      type="number"
                      step="0.05"
                      value={rxForm.velocity_stop}
                      onChange={(e) => setRxForm((prev) => ({ ...prev, velocity_stop: e.target.value }))}
                      className="bg-zinc-950 border-zinc-800"
                      placeholder="e.g., 0.45"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'IT' ? 'Note' : 'Notes'}</Label>
                    <Textarea
                      value={rxForm.notes}
                      onChange={(e) => setRxForm((prev) => ({ ...prev, notes: e.target.value }))}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-amber-500 text-zinc-950 hover:bg-amber-400">
                      {language === 'IT' ? 'Crea' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10" data-testid="delete-test-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-zinc-100">
                    {language === 'IT' ? 'Elimina Test' : 'Delete Test'}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-500">
                    {language === 'IT'
                      ? 'Questa azione non può essere annullata.'
                      : 'This action cannot be undone.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300">
                    {t('cancel')}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-rose-600 text-white hover:bg-rose-500"
                    data-testid="confirm-delete"
                  >
                    {t('delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Profile & Quality */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Load-Velocity Profile */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-blue-400" />
              {language === 'IT' ? 'Profilo Load-Velocity' : 'Load-Velocity Profile'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profile ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-950/50 rounded-sm border border-zinc-800 text-center">
                    <div className="text-xs text-zinc-500 uppercase mb-1">L0 (1RM Est.)</div>
                    <div className="text-3xl font-bold text-amber-400">{typeof profile.l0 === 'number' ? profile.l0.toFixed(1) : profile.l0} kg</div>
                  </div>
                  <div className="p-4 bg-zinc-950/50 rounded-sm border border-zinc-800 text-center">
                    <div className="text-xs text-zinc-500 uppercase mb-1">V0</div>
                    <div className="text-3xl font-bold text-blue-400">{typeof profile.v0 === 'number' ? profile.v0.toFixed(1) : profile.v0} m/s</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs text-zinc-500 uppercase">Slope</div>
                    <div className="text-lg font-medium text-zinc-100">{typeof profile.slope === 'number' ? profile.slope.toFixed(4) : profile.slope}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500 uppercase">Intercept</div>
                    <div className="text-lg font-medium text-zinc-100">{typeof profile.intercept === 'number' ? profile.intercept.toFixed(1) : profile.intercept}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500 uppercase">R²</div>
                    <div className={`text-lg font-medium ${profile.r_squared >= 0.9 ? 'text-emerald-400' : profile.r_squared >= 0.8 ? 'text-amber-400' : 'text-rose-400'}`}>
                      {typeof profile.r_squared === 'number' ? profile.r_squared.toFixed(2) : profile.r_squared}
                    </div>
                  </div>
                </div>
                {/* Simple visual representation */}
                <div className="mt-4 h-32 bg-zinc-950 rounded-sm border border-zinc-800 p-4 relative">
                  <div className="absolute inset-4 flex items-end justify-between">
                    {test.test_data.map((point, idx) => {
                      const maxLoad = Math.max(...test.test_data.map((p) => p.load_kg));
                      const maxVel = Math.max(...test.test_data.map((p) => p.v_mean));
                      const x = (point.load_kg / maxLoad) * 100;
                      const y = (point.v_mean / maxVel) * 100;
                      return (
                        <div
                          key={idx}
                          className="absolute w-3 h-3 bg-amber-500 rounded-full"
                          style={{
                            left: `${x}%`,
                            bottom: `${y}%`,
                            transform: 'translate(-50%, 50%)',
                          }}
                          title={`${typeof point.load_kg === 'number' ? point.load_kg.toFixed(1) : point.load_kg}kg @ ${typeof point.v_mean === 'number' ? point.v_mean.toFixed(1) : point.v_mean}m/s`}
                        />
                      );
                    })}
                  </div>
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-xs text-zinc-600">
                    Load (kg)
                  </div>
                  <div className="absolute left-1 top-1/2 -translate-y-1/2 text-xs text-zinc-600 -rotate-90">
                    Velocity
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                {language === 'IT' ? 'Profilo non disponibile' : 'Profile not available'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quality Report & Test Data */}
        <div className="space-y-6">
          {/* Quality Report */}
          <Card className={`bg-zinc-900/50 border ${quality?.is_valid ? 'border-emerald-500/30' : 'border-amber-500/30'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                {quality?.is_valid ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                )}
                {language === 'IT' ? 'Qualità Test' : 'Test Quality'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {quality ? (
                <div className="space-y-2">
                  {(quality.issues?.length ?? 0) > 0 && (
                    <div className="space-y-1">
                      {(quality.issues || []).map((issue, idx) => (
                        <div key={idx} className="text-sm text-rose-400 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {issue}
                        </div>
                      ))}
                    </div>
                  )}
                  {(quality.warnings?.length ?? 0) > 0 && (
                    <div className="space-y-1">
                      {(quality.warnings || []).map((warning, idx) => (
                        <div key={idx} className="text-sm text-amber-400 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {warning}
                        </div>
                      ))}
                    </div>
                  )}
                  {(quality.is_valid !== false) && (quality.issues?.length ?? 0) === 0 && (quality.warnings?.length ?? 0) === 0 && (
                    <div className="text-sm text-emerald-400">
                      {language === 'IT' ? 'Dati validi, profilo affidabile' : 'Valid data, reliable profile'}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-zinc-500 text-sm">
                  {language === 'IT' ? 'Report non disponibile' : 'Report not available'}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test Data Points */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-zinc-100">
                {language === 'IT' ? 'Dati Test' : 'Test Data'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-2 text-xs text-zinc-500 uppercase border-b border-zinc-800 pb-2">
                  <div>Load (kg)</div>
                  <div>V Mean (m/s)</div>
                  <div>V Peak (m/s)</div>
                  <div>Reps</div>
                </div>
                {test.test_data.map((point, idx) => (
                  <div key={idx} className="grid grid-cols-4 gap-2 text-sm py-2 border-b border-zinc-800/50">
                    <div className="font-medium text-zinc-100">{typeof point.load_kg === 'number' ? point.load_kg.toFixed(1) : point.load_kg}</div>
                    <div className="text-blue-400">{typeof point.v_mean === 'number' ? point.v_mean.toFixed(1) : point.v_mean}</div>
                    <div className="text-purple-400">{typeof point.v_peak === 'number' ? point.v_peak.toFixed(1) : point.v_peak}</div>
                    <div className="text-zinc-400">{point.reps}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Previous Test Comparison */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="comparison-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              {language === 'IT' ? 'Confronto con Test Precedente' : 'Previous Test Comparison'}
            </CardTitle>
            {comparison?.has_previous && (
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span>vs {comparison.previous.date}</span>
                <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">
                  {comparison.match_type === 'exercise_id' ? 'exact match' : 'lift type match'}
                </Badge>
              </div>
            )}
          </div>
          {comparison?.has_previous && comparison.previous_quality_warning && (
            <div className="text-xs text-amber-400 flex items-center gap-1 mt-1">
              <AlertCircle className="w-3 h-3" />
              <span>{language === 'IT' ? 'Test precedente:' : 'Previous test:'} {comparison.previous_quality_warning}</span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!comparison?.has_previous ? (
            <p className="text-zinc-500 text-sm py-2" data-testid="no-previous-test">
              {language === 'IT' ? 'Nessun test precedente comparabile disponibile' : 'No previous comparable test available'}
            </p>
          ) : (
            <div>
              <div className="space-y-0">
                <div className="grid grid-cols-4 gap-3 text-xs text-zinc-500 uppercase tracking-wider pb-2 border-b border-zinc-800">
                  <div>{language === 'IT' ? 'Metrica' : 'Metric'}</div>
                  <div className="text-center">{language === 'IT' ? 'Attuale' : 'Current'}</div>
                  <div className="text-center">{language === 'IT' ? 'Precedente' : 'Previous'}</div>
                  <div className="text-right">Delta</div>
                </div>
                {[
                  { key: 'l0', label: 'L0 (1RM Est.)', unit: 'kg', dec: 1 },
                  { key: 'v0', label: 'V0', unit: 'm/s', dec: 2 },
                  { key: 'slope', label: 'Slope', unit: '', dec: 4 },
                  { key: 'r_squared', label: 'R\u00b2', unit: '', dec: 2 },
                  { key: 'pmax', label: 'Pmax', unit: '', dec: 1 },
                ].map(m => {
                  const cur = comparison.current[m.key];
                  const prv = comparison.previous[m.key];
                  const delta = comparison.deltas[m.key];
                  if (cur == null && prv == null) return null;
                  const dirColor = delta?.direction === 'improved' ? 'text-emerald-400' :
                                   delta?.direction === 'declined' ? 'text-rose-400' : 'text-zinc-400';
                  const dirArrow = delta?.direction === 'improved' ? '\u2191' :
                                   delta?.direction === 'declined' ? '\u2193' : '\u2192';
                  return (
                    <div key={m.key} className="grid grid-cols-4 gap-3 text-sm py-2.5 border-b border-zinc-800/50" data-testid={`comp-row-${m.key}`}>
                      <div className="text-zinc-400 font-medium">{m.label}</div>
                      <div className="text-center font-semibold text-zinc-100">
                        {cur != null ? `${cur.toFixed(m.dec)}${m.unit ? ` ${m.unit}` : ''}` : '\u2014'}
                      </div>
                      <div className="text-center text-zinc-500">
                        {prv != null ? `${prv.toFixed(m.dec)}${m.unit ? ` ${m.unit}` : ''}` : '\u2014'}
                      </div>
                      <div className={`text-right font-medium ${dirColor}`}>
                        {delta ? (
                          <>
                            {delta.abs > 0 ? '+' : ''}{delta.abs.toFixed(m.dec)}
                            {delta.pct !== 0 && <span className="text-xs ml-1 opacity-70">({delta.pct > 0 ? '+' : ''}{delta.pct}%)</span>}
                            <span className="ml-1">{dirArrow}</span>
                          </>
                        ) : '\u2014'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-zinc-600">
                <span>{language === 'IT' ? 'Attuale:' : 'Current:'} {comparison.current.data_points} {language === 'IT' ? 'punti' : 'points'}</span>
                <span>{language === 'IT' ? 'Precedente:' : 'Previous:'} {comparison.previous.data_points} {language === 'IT' ? 'punti' : 'points'}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {test.notes && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100">{language === 'IT' ? 'Note' : 'Notes'}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-zinc-300">{test.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <div className="flex gap-4">
        <Link
          to={`/staff/vbt/athlete/${test.athlete_id}`}
          className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
        >
          {language === 'IT' ? 'Vedi Profilo VBT Atleta' : 'View Athlete VBT Profile'} →
        </Link>
      </div>
    </div>
  );
};

export default VBTTestDetailPage;
